import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api';
import { useAppStore, useAuthStore } from '@/hooks/useStore';
import { useToast } from '@/hooks/useToast';

/**
 * Logs out the exact session that initiated the request. A delayed response
 * can never clear a different account that signed in while it was pending.
 */
export function useLogout() {
  const navigate = useNavigate();
  const toast = useToast();
  const inFlightRef = useRef(false);

  return useCallback(async () => {
    if (inFlightRef.current) return;

    const authAtStart = useAuthStore.getState();
    const userId = authAtStart.user?.id;
    const token = authAtStart.token;
    if (!userId || !token) {
      authAtStart.clearAuth();
      navigate('/login', { replace: true });
      return;
    }

    const deviceToken = useAppStore.getState().pushDeviceToken;
    const finishOwnedLogout = () => {
      const currentAuth = useAuthStore.getState();
      if (currentAuth.user?.id !== userId || currentAuth.token !== token) return false;

      const currentApp = useAppStore.getState();
      if (currentApp.pushDeviceToken === deviceToken) {
        currentApp.setPushDeviceToken(null);
      }
      currentAuth.clearAuth();
      navigate('/login', { replace: true });
      return true;
    };
    inFlightRef.current = true;
    try {
      await authApi.logout(deviceToken ?? undefined, token);
      finishOwnedLogout();
    } catch (error) {
      // An unauthorized response means this bearer is already expired or
      // revoked server-side. There is no remote session left to preserve, so
      // complete the exact initiating account's local logout.
      if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        error.status === 401
      ) {
        finishOwnedLogout();
        return;
      }

      const currentAuth = useAuthStore.getState();
      if (currentAuth.user?.id === userId && currentAuth.token === token) {
        toast.error('Sign out failed. Please try again.');
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [navigate, toast]);
}
